import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { WalletContext } from '../context/WalletContex';
import TopBar from '../components/layout/TopBar';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import apiClient from '../utils/apiClient';
import {
  HiArrowDownLeft, HiArrowUpRight, HiArrowTopRightOnSquare,
  HiLockClosed, HiLockOpen, HiBanknotes, HiReceiptPercent,
  HiArrowPath,
} from 'react-icons/hi2';

const CHAINS = [
  { key: 'btc', label: 'BTC',      cls: 'btc',  symbol: 'BTC',  explorer: 'Mempool.space', explorerUrl: h => `https://mempool.space/tx/${h}` },
  { key: 'trx', label: 'TRC20',    cls: 'usdt', symbol: 'USDT', explorer: 'Tronscan',       explorerUrl: h => `https://tronscan.org/#/transaction/${h}` },
  { key: 'activity', label: 'Activity', cls: 'activity' },
];

const TYPE_META = {
  deposit:      { label: 'Deposit',        color: '#10B981', bg: 'rgba(16,185,129,0.12)', Icon: HiArrowDownLeft },
  withdrawal:   { label: 'Withdrawal',     color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  Icon: HiArrowUpRight  },
  p2p_lock:     { label: 'Trade Locked',   color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', Icon: HiLockClosed   },
  p2p_unlock:   { label: 'Trade Refunded', color: '#10B981', bg: 'rgba(16,185,129,0.12)', Icon: HiLockOpen     },
  p2p_sent:     { label: 'Trade Sent',     color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  Icon: HiArrowUpRight  },
  p2p_received: { label: 'Trade Received', color: '#10B981', bg: 'rgba(16,185,129,0.12)', Icon: HiArrowDownLeft },
  p2p_fee:      { label: 'Platform Fee',   color: '#6B7280', bg: 'rgba(107,114,128,0.12)',Icon: HiReceiptPercent },
  swap_out:     { label: 'Swap Sent',      color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  Icon: HiArrowUpRight  },
  swap_in:      { label: 'Swap Received',  color: '#10B981', bg: 'rgba(16,185,129,0.12)', Icon: HiArrowDownLeft },
  swap_fee:     { label: 'Swap Fee',       color: '#7C5CF6', bg: 'rgba(124,92,246,0.12)', Icon: HiReceiptPercent },
};

const COIN_LABEL = { btc: 'BTC', usdt: 'USDT', trx: 'TRX', bnb: 'BNB' };
const COIN_DP    = { btc: 8, usdt: 6, trx: 6, bnb: 8 };
const COIN_EXPLORER = {
  btc:  h => `https://mempool.space/tx/${h}`,
  usdt: h => `https://tronscan.org/#/transaction/${h}`,
  trx:  h => `https://tronscan.org/#/transaction/${h}`,
  bnb:  h => `https://bscscan.com/tx/${h}`,
};

export default function History() {
  const { wallet } = useContext(WalletContext);
  const [active, setActive]             = useState('activity');
  const [txs, setTxs]                   = useState([]);
  const [entries, setEntries]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [selectedTx, setSelectedTx]     = useState(null);
  const [offset, setOffset]             = useState(0);
  const [hasMore, setHasMore]           = useState(false);
  const [pendingDeposits, setPending]   = useState([]);
  const pollRef                         = useRef(null);

  const chainMeta = CHAINS.find(c => c.key === active);
  const address   = wallet?.wallets?.[active]?.address ?? '';
  const email     = wallet?.email ?? '';

  // On-chain history
  useEffect(() => {
    if (active === 'activity') return;
    if (!address) return;
    setLoading(true);
    setTxs([]);
    apiClient.get(`/history/${active}/${address}`)
      .then(r => setTxs(r.data.txs || []))
      .catch(() => setTxs([]))
      .finally(() => setLoading(false));
  }, [active, address]);

  // Internal ledger history
  const loadLedger = useCallback((reset = false) => {
    if (!email) return;
    const currentOffset = reset ? 0 : offset;
    setLoading(true);
    apiClient.get('/wallet/ledger', { params: { email, limit: 50, offset: currentOffset } })
      .then(r => {
        setEntries(prev => reset ? (r.data.entries || []) : [...prev, ...(r.data.entries || [])]);
        setHasMore(r.data.hasMore ?? false);
        setOffset(currentOffset + (r.data.entries?.length ?? 0));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [email, offset]);

  useEffect(() => {
    if (active !== 'activity') return;
    setEntries([]);
    setOffset(0);
    setHasMore(false);
    loadLedger(true);
  }, [active, email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for unconfirmed incoming deposits every 30 s while Activity tab is active
  useEffect(() => {
    if (!email || active !== 'activity') { setPending([]); return; }
    const fetch = () =>
      apiClient.get('/wallet/pending-deposits', { params: { email } })
        .then(r => setPending(r.data.pending || []))
        .catch(() => {});
    fetch();
    pollRef.current = setInterval(fetch, 30_000);
    return () => clearInterval(pollRef.current);
  }, [email, active]);

  // Poll ledger entries every 30 s while Activity tab is active
  useEffect(() => {
    if (!email || active !== 'activity') return;
    const id = setInterval(() => {
      apiClient.get('/wallet/ledger', { params: { email, limit: 50, offset: 0 } })
        .then(r => {
          setEntries(r.data.entries || []);
          setHasMore(r.data.hasMore ?? false);
          setOffset(r.data.entries?.length ?? 0);
        })
        .catch(() => {});
    }, 30_000);
    return () => clearInterval(id);
  }, [email, active]);

  const openExplorer = () => {
    const ch = CHAINS.find(c => c.key === selectedTx?.chain) || CHAINS[0];
    window.open(ch.explorerUrl(selectedTx.hash), '_blank', 'noopener,noreferrer');
    setSelectedTx(null);
  };

  const isActivity = active === 'activity';

  return (
    <div>
      <TopBar title="History" back />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 16px 20px', overflowX: 'auto' }}>
        {CHAINS.map(c => (
          <button
            key={c.key}
            onClick={() => setActive(c.key)}
            style={{
              flexShrink: 0, padding: '10px 14px', borderRadius: 14,
              border: `1px solid ${active === c.key ? 'rgba(124,92,246,0.5)' : 'rgba(255,255,255,0.07)'}`,
              background: active === c.key ? 'rgba(124,92,246,0.12)' : 'transparent',
              color: active === c.key ? '#7C5CF6' : '#8B8FA8',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Activity description */}
      {isActivity && (
        <p style={{ margin: '-8px 16px 16px', fontSize: '12px', color: '#4B5170', lineHeight: 1.5 }}>
          Every balance movement — deposits, withdrawals, trades, and swaps — with exact timestamps.
        </p>
      )}

      {/* Incoming (unconfirmed) banner */}
      {isActivity && pendingDeposits.length > 0 && (
        <div style={{ margin: '0 16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pendingDeposits.map(p => (
            <IncomingItem key={p.txid} item={p} />
          ))}
        </div>
      )}

      {/* List */}
      <div style={{ margin: '0 16px', background: '#110E30', borderRadius: 20, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', minHeight: 200 }}>
        {loading && (isActivity ? entries.length === 0 : true) && (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#4B5170', fontSize: '14px' }}>
            Loading…
          </div>
        )}

        {/* Activity (ledger) entries */}
        {isActivity && !loading && entries.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: 12 }}>📋</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: 0 }}>No activity yet</p>
          </div>
        )}
        {isActivity && entries.map((e, i) => (
          <LedgerItem
            key={e._id}
            entry={e}
            isLast={i === entries.length - 1 && !hasMore}
          />
        ))}
        {isActivity && hasMore && (
          <button
            onClick={() => loadLedger(false)}
            disabled={loading}
            style={{ width: '100%', padding: '14px', background: 'none', border: 'none', color: '#7C5CF6', fontSize: '13px', fontWeight: 600, cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}

        {/* On-chain entries */}
        {!isActivity && !loading && txs.length === 0 && (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: 12 }}>📭</div>
            <p style={{ color: '#4B5170', fontSize: '14px', margin: 0 }}>No transactions found</p>
          </div>
        )}
        {!isActivity && !loading && txs.map((tx, i) => (
          <TxItem
            key={tx.hash}
            tx={tx}
            chain={active}
            isLast={i === txs.length - 1}
            onClick={() => setSelectedTx({ ...tx, chain: active })}
          />
        ))}
      </div>

      {/* Explorer modal for on-chain txs */}
      <Modal open={!!selectedTx} onClose={() => setSelectedTx(null)} title="View on Block Explorer?">
        {selectedTx && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: '#8B8FA8', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
              You'll be taken to <strong style={{ color: '#fff' }}>{chainMeta?.explorer}</strong> to view this transaction.
            </p>
            <div style={{ background: '#1A1748', borderRadius: 12, padding: '12px 14px' }}>
              <span style={{ fontSize: '11px', color: '#4B5170', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>Transaction</span>
              <span style={{ fontSize: '12px', color: '#fff', fontFamily: 'monospace', wordBreak: 'break-all' }}>{selectedTx.hash}</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" size="md" full onClick={() => setSelectedTx(null)}>Cancel</Button>
              <Button variant="primary"   size="md" full onClick={openExplorer}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Open Explorer <HiArrowTopRightOnSquare size={14} /></span>
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ── Ledger Activity Item ─────────────────────────────── */
function LedgerItem({ entry, isLast }) {
  const meta    = TYPE_META[entry.type] || { label: entry.type, color: '#8B8FA8', bg: 'rgba(139,143,168,0.1)', Icon: HiBanknotes };
  const { Icon } = meta;
  const symbol  = COIN_LABEL[entry.coin] ?? entry.coin?.toUpperCase() ?? '?';
  const dp      = COIN_DP[entry.coin] ?? 6;
  const isIn    = entry.direction === 'in';

  const date = new Date(entry.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  const explorerBase = COIN_EXPLORER[entry.coin] ? '' : 'https://mempool.space/tx/';
  const explorerUrl  = COIN_EXPLORER[entry.coin]
    ? COIN_EXPLORER[entry.coin](entry.txid)
    : `https://mempool.space/tx/${entry.txid}`;

  return (
    <>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Type icon */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
          background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: meta.color,
        }}>
          <Icon size={18} />
        </div>

        {/* Label + detail */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{meta.label}</div>
          <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>{date}</div>
          {entry.counterparty && (
            <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 1 }}>
              {entry.direction === 'in' ? 'From' : 'To'}: {entry.counterparty}
            </div>
          )}
          {entry.note && (
            <div style={{ fontSize: '10px', color: '#4B5170', marginTop: 1, fontStyle: 'italic' }}>{entry.note}</div>
          )}
          {entry.txid && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ fontSize: '10px', color: '#7C5CF6', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3, textDecoration: 'none' }}
            >
              {entry.txid.slice(0, 10)}…{entry.txid.slice(-6)}
              <HiArrowTopRightOnSquare size={10} />
            </a>
          )}
        </div>

        {/* Amount + balance */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: isIn ? '#10B981' : '#EF4444' }}>
            {isIn ? '+' : '-'}{entry.amount.toFixed(dp)} {symbol}
          </div>
          <div style={{ fontSize: '10px', color: '#4B5170', marginTop: 3 }}>
            Balance: {entry.balanceAfter.toFixed(dp)} {symbol}
          </div>
        </div>
      </div>
      {!isLast && <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '0 16px' }} />}
    </>
  );
}

/* ── On-chain Tx Item (unchanged) ─────────────────────── */
function TxItem({ tx, chain, isLast, onClick }) {
  const received = tx.direction === 'received';
  const symbol   = chain === 'btc' ? 'BTC' : 'USDT';
  const date     = new Date(tx.timestamp).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  return (
    <>
      <button
        onClick={onClick}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div className="tx-item" style={{ padding: '14px 16px' }}>
          <div className={`tx-icon ${received ? 'received' : 'sent'}`}>
            {received ? <HiArrowDownLeft size={18} /> : <HiArrowUpRight size={18} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
              {received ? 'Received' : 'Sent'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
              <div style={{ fontSize: '11px', color: '#4B5170', fontFamily: 'monospace' }}>
                {tx.hash.slice(0, 8)}…{tx.hash.slice(-6)}
              </div>
              <span className={`badge ${tx.status === 'confirmed' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '10px', padding: '1px 6px' }}>
                {tx.status}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: received ? '#10B981' : '#EF4444' }}>
              {received ? '+' : '-'}{tx.value} {symbol}
            </div>
            <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 2 }}>{date}</div>
          </div>
          <HiArrowTopRightOnSquare size={14} style={{ color: '#4B5170', flexShrink: 0, marginLeft: 4 }} />
        </div>
      </button>
      {!isLast && <div className="divider" style={{ margin: '0 16px' }} />}
    </>
  );
}

/* ── Incoming (unconfirmed) deposit item ──────────────────────────────────── */
function IncomingItem({ item }) {
  const symbol = COIN_LABEL[item.coin] ?? item.coin?.toUpperCase();
  const dp     = COIN_DP[item.coin] ?? 8;
  const url    = COIN_EXPLORER[item.coin]?.(item.txid);

  const confirmLabel = item.required > 1
    ? `${item.confirmations}/${item.required} confirmations`
    : 'Waiting for confirmation';

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(16,185,129,0.04))',
      border: '1px solid rgba(16,185,129,0.25)',
      borderRadius: 16,
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      {/* Pulsing dot */}
      <div style={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: 'rgba(16,185,129,0.15)',
          animation: 'pulse-ring 2s cubic-bezier(0.215,0.61,0.355,1) infinite',
        }} />
        <div style={{
          position: 'absolute', inset: 6, borderRadius: '50%',
          background: '#10B981',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <HiArrowPath size={14} style={{ color: '#fff', animation: 'spin 2s linear infinite' }} />
        </div>
        <style>{`
          @keyframes pulse-ring {
            0%   { transform: scale(0.85); opacity: 0.8; }
            70%  { transform: scale(1.15); opacity: 0; }
            100% { transform: scale(0.85); opacity: 0; }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
        `}</style>
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>Incoming</span>
          <span style={{
            fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: 20,
            background: 'rgba(16,185,129,0.15)', color: '#10B981',
            border: '1px solid rgba(16,185,129,0.3)',
          }}>{symbol}</span>
        </div>
        <div style={{ fontSize: '11px', color: '#4B5170', marginTop: 3 }}>{confirmLabel}</div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: '10px', color: '#7C5CF6', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3, textDecoration: 'none' }}
          >
            {item.txid.slice(0, 10)}…{item.txid.slice(-6)}
            <HiArrowTopRightOnSquare size={10} />
          </a>
        )}
      </div>

      {/* Amount */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: '#10B981' }}>
          +{item.amount.toFixed(dp)} {symbol}
        </div>
        <div style={{ fontSize: '10px', color: '#4B5170', marginTop: 2 }}>Pending credit</div>
      </div>
    </div>
  );
}
